/**
 * 포인트 출금 (`/mypage/withdraw`, 시안 `_withdraw.html`).
 *
 * ★ **백엔드가 없다.** 출금 컨트롤러도, 출금 신청 테이블도, 회원 계좌 정보도 없다.
 *   (`MC_USER` 에 계좌 컬럼이 없고, `T_WITHDRAW_REQUEST` 는 구 shopcast 의 해지신청
 *   테이블이라 무관하다.) 화면은 시안대로 옮기고 신청은 안내만 띄운다.
 *
 * 이식할 때 필요한 것:
 *
 *   | 항목        | 필요한 것                                                     |
 *   | ----------- | ------------------------------------------------------------- |
 *   | 출금 계좌   | `MC_USER` 에 은행코드·계좌번호·예금주 컬럼 (또는 별도 테이블)  |
 *   | 은행 목록   | `T_BANKCODE` (24건) 를 그대로 쓸 수 있다                       |
 *   | 출금 신청   | 신청 테이블 + `REWARD.WITHDRAWAL` 포인트 차감                  |
 *   | 출금 가능액 | `MC_USER_POINT` 의 `F_USED` / `F_LIMIT_DATE` 로 계산           |
 *
 * 포인트 코드는 이미 정의돼 있다 — `Core/Vars/Point.php::RewardWithDrawal`
 * (`REWARD.WITHDRAWAL`).
 */

/**
 * 송금 수수료율.
 *
 * 정본은 백엔드 `ModuleWithdraw::TRANSFER_FEE_RATE` 이고, 출금 API 의 `info` 가
 * `policy.feeRate` 로 내려준다. 여기 값은 **응답이 오기 전에 화면을 그릴 때만**
 * 쓰는 초기값이다.
 *
 * ⚠️ 예전에는 500원 정액이었다. 비율로 바뀌어 금액이 커지면 수수료도 커진다 —
 * 5,000P 는 500원, 50,000P 는 5,000원이다.
 */
export const TRANSFER_FEE_RATE = 0.1;

/** 신청 금액에 붙는 수수료(원). 원 단위로 버린다. */
export function transferFee(point: number, rate: number = TRANSFER_FEE_RATE): number {
  return Math.floor(point * POINT_TO_WON * rate);
}

/**
 * 1P 당 환산 금액(원).
 *
 * 시안이 `50,000P` → `49,500원`(수수료 500 제외)으로 표시하므로 1:1 이다.
 */
export const POINT_TO_WON = 1;

export const WITHDRAW_TITLE = '포인트 출금 신청';
export const WITHDRAW_AMOUNT_LABEL = '출금 신청할 포인트';
export const DEPOSIT_LABEL = '입금 예정액';
export const DEPOSIT_NOTE = `송금 수수료 ${Math.round(TRANSFER_FEE_RATE * 100)}% 제외`;
export const SUBMIT_LABEL = '출금 신청하기';
export const MAX_KEY_LABEL = '최대금액';

/** 계좌가 등록돼 있지 않을 때 (시안은 `KB국민(1234) 홍*동` 예시가 박혀 있다) */
export const NO_ACCOUNT_TEXT = '출금 계좌를 등록해 주세요';

/** 키패드 배열. 시안 순서 그대로. `max`/`del` 은 기능 키다. */
export const KEYPAD: (string | 'max' | 'del')[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'max',
  '0',
  'del',
];

/** 입력 상한 자릿수. 실수로 억 단위가 찍히는 것을 막는다. */
export const MAX_INPUT_DIGITS = 9;

/**
 * 은행 이름 → 로고 파일.
 *
 * ★ 키는 **DB(`T_BANKCODE`)의 이름 그대로**다. 시안은 'KB국민'·'신한' 처럼 짧게
 *   적었지만 서버가 주는 값은 '국민은행'·'신한은행' 이다. 시안 이름으로 키를 만들면
 *   목록 대부분이 로고 없이 나온다(실제로 그렇게 됐다).
 *
 * ★ 같은 로고를 나눠 쓰는 곳이 있다 — 부산·경남(BNK), 광주·전북(JB), 신한·제주.
 *   시안이 그렇게 짝지어 두었다.
 *
 * 로고가 없는 곳(증권사)은 이름만 나온다. 목록에서 빠지지는 않는다.
 */
export const BANK_LOGO: Record<string, string> = {
  국민은행: 'kookmin-381.png',
  신한은행: 'shinhan-366.png',
  농협은행: 'nh-371.png',
  하나은행: 'hana-374.png',
  우리은행: 'woori-041.png',
  기업은행: 'ibk-225.png',
  카카오뱅크: 'kakao-090.png',
  토스뱅크: 'toss-092.png',
  케이뱅크: 'kbank-089.png',
  우체국: 'post-071.png',
  새마을금고: 'mg-045.png',
  신협중앙회: 'cu-048.png',
  수협중앙회: 'sh-369.png',
  부산은행: 'bnk-224.png',
  경남은행: 'bnk-224.png',
  광주은행: 'kjb-364.png',
  전북은행: 'kjb-364.png',
  제주은행: 'shinhan-366.png',
  한국씨티은행: 'citi-370.png',
  SC은행: 'sc-023.png',
  산업은행: 'kdb-002.png',
  아이엠뱅크: 'im-031.png',
  저축은행: 'sb-050.png',
  HSBC은행: 'hsbc-054.png',
};

/** 로고 파일 경로. 없는 은행이면 null */
export function bankLogo(name?: string | null): string | null {
  const file = BANK_LOGO[String(name ?? '')];
  return file ? `/images/${file}` : null;
}

/**
 * DB 이름 → 화면에 쓸 짧은 이름.
 *
 * `T_BANKCODE` 는 '국민은행'·'한국씨티은행' 처럼 정식 명칭을 담고 있는데, 시안은
 * 로고 아래에 'KB국민'·'씨티' 처럼 짧게 적는다. 4칸 격자라 긴 이름은 줄바꿈된다.
 *
 * ★ **저장에는 쓰지 않는다.** 서버로는 언제나 DB 코드를 보낸다.
 */
const BANK_LABEL: Record<string, string> = {
  국민은행: 'KB국민',
  신한은행: '신한',
  농협은행: 'NH농협',
  하나은행: '하나',
  우리은행: '우리',
  기업은행: 'IBK기업',
  아이엠뱅크: 'iM뱅크',
  신협중앙회: '신협',
  수협중앙회: '수협',
  부산은행: '부산',
  경남은행: '경남',
  광주은행: '광주',
  전북은행: '전북',
  제주은행: '제주',
  한국씨티은행: '씨티',
  SC은행: 'SC제일',
  산업은행: 'KDB산업',
  HSBC은행: 'HSBC',
  저축은행: '저축',
};

/** 화면에 보여줄 은행 이름. 지도에 없으면 DB 이름 그대로. */
export function bankLabel(name?: string | null): string {
  const value = String(name ?? '');
  return BANK_LABEL[value] ?? value;
}

/**
 * 시안의 노출 순서 (DB 이름 기준).
 *
 * `T_BANKCODE.F_ORDER` 를 쓰지 않는다. **그 표는 이 서비스 전용이 아니다** —
 * 결제·CMS 가 함께 쓰는 구 운영 DB 의 표라, 우리 화면 순서 때문에 정렬값을 바꾸면
 * 다른 서비스의 목록까지 흔들린다. 순서는 여기서 정한다.
 */
const BANK_ORDER = [
  '국민은행',
  '신한은행',
  '농협은행',
  '하나은행',
  '우리은행',
  '카카오뱅크',
  '토스뱅크',
  '기업은행',
  '우체국',
  '아이엠뱅크',
  '케이뱅크',
  '새마을금고',
  '신협중앙회',
  '부산은행',
  '경남은행',
  '광주은행',
  '전북은행',
  '제주은행',
  'SC은행',
  '한국씨티은행',
  '수협중앙회',
  '저축은행',
  '산업은행',
  'HSBC은행',
];

/**
 * 이 서비스에서는 감출 곳.
 *
 * 시안 목록에 없다. 표에서 지우지는 않는다 — 자동이체(CMS) 기록이 그 코드로
 * 은행을 찾고 있어서, 지우면 그쪽이 은행명을 잃는다.
 */
const BANK_HIDDEN = new Set(['삼성증권', '유안타증권']);

/**
 * 화면에 뿌릴 은행 목록.
 *
 * 시안 순서대로 세우고, 시안에 없는 곳은 감춘다. 목록에 없는 이름이 나중에
 * `T_BANKCODE` 에 추가되면 **맨 뒤에 붙는다** — 조용히 사라지지 않게.
 */
export function sortBanks<T extends { name: string }>(banks: T[]): T[] {
  const rank = (name: string) => {
    const index = BANK_ORDER.indexOf(name);
    return index < 0 ? BANK_ORDER.length : index;
  };

  return banks
    .filter((bank) => !BANK_HIDDEN.has(bank.name))
    .slice()
    .sort((a, b) => rank(a.name) - rank(b.name));
}
